/**
 * Resolves a currency identifier (UUID or ISO code) to an ISO code
 * This is a fallback function that checks if the input looks like a UUID
 * For proper resolution, use useCurrencyMap hook in components
 */
const resolveCurrencyCode = (currency?: string): string => {
  if (!currency) return "USD";
  
  // If it's already a valid ISO code (3 uppercase letters), return it
  if (/^[A-Z]{3}$/.test(currency)) {
    return currency;
  }
  
  // If it looks like a UUID, we can't resolve it here without the map
  // Return USD as fallback - components should use useCurrencyMap hook
  if (currency.includes("-") && currency.length > 30) {
    console.warn(`Currency UUID detected but not resolved: ${currency}. Use useCurrencyMap hook for proper resolution.`);
    return "USD";
  }
  
  // Otherwise, assume it's already a code (maybe lowercase)
  return currency.toUpperCase();
};

export const formatCurrency = (value?: number, currency = "USD", decimals?: number): string => {
  if (value === undefined || value === null) return "-";
  const resolvedCurrency = resolveCurrencyCode(currency);
  
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: resolvedCurrency,
      minimumFractionDigits: decimals ?? (value < 1 ? 4 : 2),
      maximumFractionDigits: decimals ?? (value < 1 ? 4 : 2),
    }).format(value);
  } catch {
    // Fallback if currency code is invalid
    console.warn(`Invalid currency code: ${resolvedCurrency}, falling back to USD`);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimals ?? (value < 1 ? 4 : 2),
      maximumFractionDigits: decimals ?? (value < 1 ? 4 : 2),
    }).format(value);
  }
};

export const formatLargeCurrency = (value?: number, currency = "USD"): string => {
  if (value === undefined || value === null) return "-";
  const resolvedCurrency = resolveCurrencyCode(currency);
  
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: resolvedCurrency,
      notation: "compact",
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    console.warn(`Invalid currency code: ${resolvedCurrency}, falling back to USD`);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 2,
    }).format(value);
  }
};

export const formatPrice = (value?: number, currency = "USD"): string => {
  if (value === undefined || value === null) return "-";
  const resolvedCurrency = resolveCurrencyCode(currency);
  const decimals = value < 1 ? 6 : value < 100 ? 4 : 2;
  
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: resolvedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch {
    console.warn(`Invalid currency code: ${resolvedCurrency}, falling back to USD`);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: decimals,
    }).format(value);
  }
};

export const getCurrencySymbol = (currency = "USD"): string => {
  const resolvedCurrency = resolveCurrencyCode(currency);
  try {
    return (0).toLocaleString("en-US", {
      style: "currency",
      currency: resolvedCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).replace(/\d/g, "").trim();
  } catch {
    return resolvedCurrency; // Fallback to code if invalid
  }
};

export const getCurrencyName = (currency = "USD"): string => {
  const resolvedCurrency = resolveCurrencyCode(currency);
  try {
    return new Intl.DisplayNames(["en-US"], { type: "currency" }).of(resolvedCurrency) || resolvedCurrency;
  } catch {
    return resolvedCurrency;
  }
};
